import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Clock, TrendingUp, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { supabaseReplica, shouldUseReplica } from "@/integrations/supabase/replica";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import Highcharts from "highcharts";

interface HourlyTimelineRow {
  hour_bucket: string;
  total_records: number;
  hospital_count: number;
  hospital_details: {
    hospital_name: string;
    city: string;
    state: string;
    record_count: number;
  }[];
}

interface KpiSummary {
  records_1h: number;
  hospitals_1h: number;
  records_6h: number;
  hospitals_6h: number;
  records_24h: number;
  hospitals_24h: number;
  records_48h: number;
  hospitals_48h: number;
  peak_hour: string;
  peak_hour_records: number;
}

export default function HourlyIngestionTimeline() {
  const chartRef = useRef<HTMLDivElement>(null);
  const [hoursBack, setHoursBack] = useState(48);
  const [mounted, setMounted] = useState(false);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAHP = brand.key === "ahp";
  const isAHPDark = isAHP && isDark;

  useEffect(() => {
    setMounted(true);
  }, []);

  const client = shouldUseReplica("analytics") ? supabaseReplica : supabase;

  const { data: timelineData } = useQuery({
    queryKey: ["hourly-ingestion-timeline", hoursBack],
    queryFn: async () => {
      const { data, error } = await client.rpc("get_hourly_ingestion_timeline", {
        p_hours_back: hoursBack,
      });
      if (error) throw error;
      return data as unknown as HourlyTimelineRow[];
    },
    refetchInterval: 60_000,
  });

  const { data: kpiData } = useQuery({
    queryKey: ["ingestion-kpi-summary"],
    queryFn: async () => {
      const { data, error } = await client.rpc("get_ingestion_kpi_summary");
      if (error) throw error;
      return (data as unknown as KpiSummary[])?.[0] ?? null;
    },
    refetchInterval: 30_000,
  });

  // Render chart
  useEffect(() => {
    if (!timelineData || !mounted || !chartRef.current) return;

    const categories = timelineData.map((d) => {
      const dt = new Date(d.hour_bucket);
      return dt.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        hour12: true,
      });
    });

    const recordSeries = timelineData.map((d) => ({
      y: d.total_records,
      custom: { details: d.hospital_details },
    }));

    const hospitalSeries = timelineData.map((d) => d.hospital_count);

    // Brand-aware colors: AHP dark → AHP light → Luigi's
    const colBar = isAHPDark ? "#5B9BD5" : isAHP ? "#1a3a6b" : "#06b6d4";
    const colBarFill0 = isAHPDark ? "rgba(91,155,213,0.8)" : isAHP ? "rgba(26,58,107,0.8)" : "rgba(6,182,212,0.9)";
    const colBarFill1 = isAHPDark ? "rgba(91,155,213,0.2)" : isAHP ? "rgba(26,58,107,0.25)" : "rgba(6,182,212,0.4)";
    const colLine = isAHPDark ? "#C9A961" : isAHP ? "#B22234" : "#f59e0b";
    const colTitle = isAHPDark ? "#FAF9F6" : isAHP ? "#1a3a6b" : "#e2e8f0";
    const colSub = isAHPDark ? "rgba(250,249,246,0.55)" : isAHP ? "#4a5568" : "#94a3b8";
    const colLabel = isAHPDark ? "rgba(250,249,246,0.55)" : isAHP ? "#4a5568" : "#94a3b8";
    const colGrid = isAHPDark ? "rgba(250,249,246,0.1)" : isAHP ? "#e2e8f0" : "#374151";
    const colTooltipBg = isAHPDark ? "#0d1e35" : isAHP ? "#ffffff" : "#1f2937";
    const colTooltipBorder = isAHPDark ? "#5B9BD5" : isAHP ? "#1a3a6b" : "#374151";
    const colTooltipText = isAHPDark ? "#FAF9F6" : isAHP ? "#1a3a6b" : "#e2e8f0";
    const colLegend = isAHPDark ? "#FAF9F6" : isAHP ? "#1a3a6b" : "#e2e8f0";
    const colZoomBtn = isAHPDark ? "#0d1e35" : isAHP ? "#f8f6f1" : "#1f2937";
    const colZoomStroke = isAHPDark ? "#5B9BD5" : isAHP ? "#1a3a6b" : "#06b6d4";

    Highcharts.chart(chartRef.current, {
      chart: {
        backgroundColor: "transparent",
        zoomType: "x",
        panning: { enabled: true, type: "x" } as any,
        panKey: "shift",
        resetZoomButton: {
          theme: {
            fill: colZoomBtn,
            stroke: colZoomStroke,
            style: { color: colTitle },
            r: 6,
          },
          position: { align: "right", verticalAlign: "top", x: -10, y: 10 },
        },
        style: { fontFamily: "inherit" },
      },
      title: {
        text: "Records Ingested Per Hour",
        style: { color: colTitle, fontSize: "16px", fontWeight: "600" },
      },
      subtitle: {
        text: "Click and drag to zoom \u00B7 Shift+drag to pan",
        style: { color: colSub, fontSize: "12px" },
      },
      xAxis: {
        categories,
        labels: {
          style: { color: colLabel, fontSize: "10px" },
          rotation: -45,
          step: Math.max(1, Math.floor(categories.length / 12)),
        },
        gridLineColor: colGrid,
        lineColor: colGrid,
        tickColor: colGrid,
      },
      yAxis: [
        {
          title: { text: "Records", style: { color: colBar } },
          labels: {
            style: { color: colLabel },
            formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
              const v = this.value as number;
              if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
              if (v >= 1_000) return (v / 1_000).toFixed(0) + "K";
              return String(v);
            },
          },
          gridLineColor: colGrid,
        },
        {
          title: { text: "Hospitals", style: { color: colLine } },
          labels: { style: { color: colLabel } },
          opposite: true,
          gridLineWidth: 0,
        },
      ],
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: colTooltipBg,
        borderColor: colTooltipBorder,
        style: { color: colTooltipText },
        formatter: function (this: any) {
          const point = this.points?.[0]?.point;
          const details: HourlyTimelineRow["hospital_details"] =
            point?.custom?.details || [];
          const top5 = details.slice(0, 5);
          const sub = isAHPDark ? "rgba(250,249,246,0.45)" : isAHP ? "#718096" : "#94a3b8";
          const hr = isAHPDark ? "rgba(250,249,246,0.1)" : isAHP ? "#e2e8f0" : "#374151";
          let html = `<div style="padding:4px 2px"><b style="color:${colBar}">${this.x}</b><br/>`;
          html += `<span style="color:${colBar}">\u25CF</span> Records: <b>${(this.points?.[0]?.y ?? 0).toLocaleString()}</b><br/>`;
          html += `<span style="color:${colLine}">\u25CF</span> Hospitals: <b>${this.points?.[1]?.y ?? 0}</b>`;
          if (top5.length > 0) {
            html += `<br/><hr style="border-color:${hr};margin:4px 0"/>`;
            html += `<span style="color:${sub};font-size:11px">Top hospitals:</span><br/>`;
            top5.forEach((h) => {
              html += `<span style="font-size:11px">${h.hospital_name} <span style="color:${sub}">(${h.state})</span> \u2014 <b>${h.record_count.toLocaleString()}</b></span><br/>`;
            });
            if (details.length > 5) {
              html += `<span style="color:${sub};font-size:10px">+${details.length - 5} more</span>`;
            }
          }
          html += `</div>`;
          return html;
        },
      },
      legend: {
        itemStyle: { color: colLegend },
        itemHoverStyle: { color: isAHPDark ? "#ffffff" : isAHP ? "#000000" : "#ffffff" },
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          borderRadius: 3,
        },
        spline: {
          lineWidth: 2,
          marker: { enabled: true, radius: 3, fillColor: colLine },
        },
      },
      series: [
        {
          type: "column",
          name: "Records",
          data: recordSeries,
          color: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, colBarFill0],
              [1, colBarFill1],
            ],
          } as any,
          yAxis: 0,
        },
        {
          type: "spline",
          name: "Hospitals",
          data: hospitalSeries,
          color: colLine,
          yAxis: 1,
        },
      ],
      credits: { enabled: false },
    } as any);
  }, [timelineData, mounted, isAHP, isDark]);

  // Brand-aware KPI card styles
  const kpiCards = kpiData
    ? [
        {
          label: "Last Hour",
          records: kpiData.records_1h,
          hospitals: kpiData.hospitals_1h,
          icon: Zap,
          cardClass: isAHPDark ? "bg-[#0d1e35] border-[#B22234]/20" : isAHP ? "bg-white/90 border-[#B22234]/20 shadow-sm" : "bg-black/40 border-cyan-500/30",
          textClass: isAHPDark ? "text-[#e57373]" : isAHP ? "text-[#B22234]" : "text-cyan-400",
          iconClass: isAHPDark ? "text-[#e57373]" : isAHP ? "text-[#B22234]" : "text-cyan-500",
        },
        {
          label: "Last 6 Hours",
          records: kpiData.records_6h,
          hospitals: kpiData.hospitals_6h,
          icon: Activity,
          cardClass: isAHPDark ? "bg-[#0d1e35] border-emerald-500/20" : isAHP ? "bg-white/90 border-[#2b5e49]/20 shadow-sm" : "bg-black/40 border-emerald-500/30",
          textClass: isAHPDark ? "text-emerald-400" : isAHP ? "text-[#2b5e49]" : "text-emerald-400",
          iconClass: isAHPDark ? "text-emerald-400" : isAHP ? "text-[#2b5e49]" : "text-emerald-500",
        },
        {
          label: "Last 24 Hours",
          records: kpiData.records_24h,
          hospitals: kpiData.hospitals_24h,
          icon: Clock,
          cardClass: isAHPDark ? "bg-[#0d1e35] border-[#C9A961]/20" : isAHP ? "bg-white/90 border-[#C9A961]/30 shadow-sm" : "bg-black/40 border-amber-500/30",
          textClass: isAHPDark ? "text-[#C9A961]" : isAHP ? "text-[#C9A961]" : "text-amber-400",
          iconClass: isAHPDark ? "text-[#C9A961]" : isAHP ? "text-[#C9A961]" : "text-amber-500",
        },
        {
          label: "Last 48 Hours",
          records: kpiData.records_48h,
          hospitals: kpiData.hospitals_48h,
          icon: TrendingUp,
          cardClass: isAHPDark ? "bg-[#0d1e35] border-[#5B9BD5]/20" : isAHP ? "bg-white/90 border-[#1a3a6b]/20 shadow-sm" : "bg-black/40 border-violet-500/30",
          textClass: isAHPDark ? "text-[#5B9BD5]" : isAHP ? "text-[#1a3a6b]" : "text-violet-400",
          iconClass: isAHPDark ? "text-[#5B9BD5]" : isAHP ? "text-[#1a3a6b]" : "text-violet-500",
        },
      ]
    : [];

  const peakHourLabel = kpiData?.peak_hour
    ? new Date(kpiData.peak_hour).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        hour12: true,
      })
    : null;

  return (
    <div className="mb-12 space-y-6">
      {/* KPI Cards */}
      {kpiData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((kpi) => (
            <Card key={kpi.label} className={kpi.cardClass}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className={`text-sm font-medium ${isAHPDark ? "text-[#FAF9F6]/60" : isAHP ? "text-[#4a5568]" : "text-muted-foreground"}`}>
                  {kpi.label}
                </CardTitle>
                <kpi.icon className={`h-4 w-4 ${kpi.iconClass}`} />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${kpi.textClass}`}>
                  {kpi.records.toLocaleString()}
                </div>
                <p className={`text-xs ${isAHPDark ? "text-[#FAF9F6]/45" : isAHP ? "text-[#718096]" : "text-muted-foreground"}`}>
                  records from {kpi.hospitals} hospital{kpi.hospitals !== 1 ? "s" : ""}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Chart */}
      <Card className={isAHPDark ? "bg-[#0d1e35] border-[#FAF9F6]/10" : isAHP ? "bg-white/90 border-[#1a3a6b]/10 shadow-sm" : "bg-black/40 border-muted"}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className={`text-lg font-semibold ${isAHPDark ? "text-[#FAF9F6]" : isAHP ? "text-[#1a3a6b]" : "text-slate-200"}`}>
            Hourly Ingestion Timeline
          </CardTitle>
          <div className="flex gap-2">
            {[24, 48, 72].map((h) => (
              <Button
                key={h}
                size="sm"
                variant={hoursBack === h ? "default" : "outline"}
                className={
                  hoursBack === h
                    ? isAHPDark
                      ? "bg-[#5B9BD5] hover:bg-[#4a8ac4] text-[#0a1628] text-xs"
                      : isAHP
                      ? "bg-[#1a3a6b] hover:bg-[#14305a] text-white text-xs"
                      : "bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                    : isAHPDark
                      ? "border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 text-xs"
                      : isAHP
                      ? "border-[#1a3a6b]/30 text-[#1a3a6b] hover:bg-[#1a3a6b]/5 text-xs"
                      : "border-slate-600 text-slate-400 hover:text-white text-xs"
                }
                onClick={() => setHoursBack(h)}
              >
                {h}h
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div ref={chartRef} style={{ width: "100%", minHeight: 400 }} />
        </CardContent>
      </Card>

      {/* Peak Hour Callout */}
      {kpiData?.peak_hour_records && kpiData.peak_hour_records > 0 && (
        <div className={`flex items-center gap-3 rounded-lg px-4 py-3 ${
          isAHPDark
            ? "bg-[#5B9BD5]/5 border border-[#5B9BD5]/15"
            : isAHP
            ? "bg-[#1a3a6b]/5 border border-[#1a3a6b]/15"
            : "bg-cyan-950/30 border border-cyan-500/20"
        }`}>
          <Zap className={`h-5 w-5 shrink-0 ${isAHPDark ? "text-[#C9A961]" : isAHP ? "text-[#C9A961]" : "text-cyan-400"}`} />
          <p className={`text-sm ${isAHPDark ? "text-[#FAF9F6]/60" : isAHP ? "text-[#4a5568]" : "text-slate-300"}`}>
            <span className={`font-semibold ${isAHPDark ? "text-[#FAF9F6]" : isAHP ? "text-[#1a3a6b]" : "text-cyan-400"}`}>Peak hour:</span>{" "}
            {peakHourLabel} with{" "}
            <span className={`font-bold ${isAHPDark ? "text-[#C9A961]" : isAHP ? "text-[#B22234]" : "text-cyan-300"}`}>
              {kpiData.peak_hour_records.toLocaleString()}
            </span>{" "}
            records ingested
          </p>
        </div>
      )}
    </div>
  );
}
